import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  RefreshCw, ArrowLeft, TrendingUp, TrendingDown, Wallet, Percent, ArrowUpRight, Banknote, CalendarDays,
} from 'lucide-react';
import { useWS } from '../WSContext';

// ── Tipler (backend get_navi_snapshots ile eşleşir) ───────────────────────────
interface NaviAsset {
  symbol: string; assetId: number;
  supply: number; borrow: number;
  priceUsd: number | null; supplyUsd: number | null; borrowUsd: number | null;
  // scaled + endeks AYRI gelir (string: u128 taşma yok) → faiz/kullanıcı ayrışması bunlarla yapılır
  scaledSupply: string; scaledBorrow: string;
  supplyIndex: string; borrowIndex: string;
}
interface NaviSnapshot {
  ts: number; assets: NaviAsset[];
  supplyUsd: number; borrowUsd: number; netUsd: number;
}

// ── Yardımcılar ───────────────────────────────────────────────────────────────
const fmt = (n: number, d = 4) =>
  (n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: d });
const fmtUsd = (n: number) =>
  '$' + (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtSigned = (n: number, d = 4) => (n > 0 ? '+' : '') + fmt(n, d);
const fmtDate = (ms: number) =>
  ms ? new Date(ms).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';
const shortId = (s: string) => (s ? `${s.slice(0, 6)}…${s.slice(-4)}` : '');

/** İki snapshot arası değişimi FAİZ ve KULLANICI HAREKETİ olarak ayrıştırır.
 *  Navi muhasebesi: gerçek = scaled × endeks.
 *   • scaled sabit  → değişimin tamamı faiz
 *   • scaled değişti → aradaki fark kullanıcı işlemi (yatırma/çekme)
 *  Faiz = eski scaled'ın YENİ endeksle değeri − eski gerçek değer. */
const RAY = BigInt("1000000000000000000000000000");   // 1e27 (es5 hedefi: 10n literal / ** yok)
const NAVI_SCALE = 1e9;
const ZERO = BigInt(0);
interface Decomp { symbol: string; interest: number; user: number; kind: 'supply' | 'borrow'; }
function decompose(first: NaviSnapshot, last: NaviSnapshot): Decomp[] {
  const out: Decomp[] = [];
  for (const a of last.assets) {
    const f = first.assets.find(x => x.assetId === a.assetId);
    if (!f) continue;
    for (const kind of ['supply', 'borrow'] as const) {
      const sup = kind === 'supply';
      const scOld = BigInt((sup ? f.scaledSupply : f.scaledBorrow) || '0');
      const scNew = BigInt((sup ? a.scaledSupply : a.scaledBorrow) || '0');
      const ixOld = BigInt((sup ? f.supplyIndex : f.borrowIndex) || '0');
      const ixNew = BigInt((sup ? a.supplyIndex : a.borrowIndex) || '0');
      if ((scOld === ZERO && scNew === ZERO) || ixOld === ZERO || ixNew === ZERO) continue;
      const oldReal = (scOld * ixOld) / RAY;
      const midReal = (scOld * ixNew) / RAY;   // eski scaled + yeni endeks = saf faiz etkisi
      const newReal = (scNew * ixNew) / RAY;
      const interest = Number(midReal - oldReal) / NAVI_SCALE;
      const user = Number(newReal - midReal) / NAVI_SCALE;
      if (Math.abs(interest) < 1e-9 && Math.abs(user) < 1e-9) continue;
      out.push({ symbol: a.symbol, interest, user, kind });
    }
  }
  return out.sort((a, b) => Math.abs(b.interest) - Math.abs(a.interest));
}

// ── Basit SVG çizgi grafiği (harici bağımlılık yok) ───────────────────────────
function Sparkline({ points, dark, color = '#38bdf8', height = 120 }:
  { points: { ts: number; v: number }[]; dark: boolean; color?: string; height?: number }) {
  if (points.length < 2) return null;
  const W = 100, H = 100;   // viewBox birimleri; preserveAspectRatio ile esner
  const vs = points.map(p => p.v);
  const min = Math.min(...vs), max = Math.max(...vs);
  const span = max - min || Math.abs(max) || 1;
  const pad = span * 0.12;
  const lo = min - pad, hi = max + pad;
  const x = (i: number) => (i / (points.length - 1)) * W;
  const y = (v: number) => H - ((v - lo) / (hi - lo)) * H;
  const d = points.map((p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(2)},${y(p.v).toFixed(2)}`).join(' ');
  const area = `${d} L${W},${H} L0,${H} Z`;
  return (
    <div style={{ height }} className="w-full">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-full">
        <defs>
          <linearGradient id="navifill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.28" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#navifill)" />
        <path d={d} fill="none" stroke={color} strokeWidth="0.8" vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
}

interface Props { dark?: boolean }

export default function NaviTrax({ dark = true }: Props) {
  const { sendWs, subscribe, connected } = useWS() as any;

  const [snaps, setSnaps] = useState<NaviSnapshot[]>([]);
  const [wallet, setWallet] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    sendWs?.({ type: 'get_navi_snapshots', payload: {} });
  }, [sendWs]);

  useEffect(() => { if (connected) load(); }, [connected, load]);

  useEffect(() => {
    const unsub = subscribe?.((msg: any) => {
      if (msg.type === 'navi_snapshots') {
        setLoading(false);
        setSnaps(msg.payload?.snapshots ?? []);
        setWallet(msg.payload?.wallet ?? '');
      }
    });
    return () => unsub?.();
  }, [subscribe]);

  // ── Tema ────────────────────────────────────────────────────────────────────
  const bg    = dark ? 'bg-[#03112b]' : 'bg-[#f4f7fb]';
  const card  = dark ? 'bg-[#061d3f] border-[#0a2560]' : 'bg-white border-[#d4e0f0]';
  const sub   = dark ? 'bg-[#03112b]/60 border-[#0a2560]' : 'bg-[#eef3fa] border-[#d4e0f0]';
  const text  = dark ? 'text-blue-50' : 'text-slate-800';
  const muted = dark ? 'text-blue-300/70' : 'text-slate-500';

  const latest = snaps.length ? snaps[snaps.length - 1] : null;
  const first  = snaps.length ? snaps[0] : null;

  const series = useMemo(() => snaps.map(s => ({ ts: s.ts, v: s.netUsd })), [snaps]);
  const decomp = useMemo(
    () => (first && latest && first !== latest ? decompose(first, latest) : []),
    [first, latest]);
  const netChange = first && latest ? latest.netUsd - first.netUsd : 0;

  return (
    <div className={`min-h-screen ${bg} ${text} p-4 md:p-6`}>
      <div className="max-w-6xl mx-auto">
        {/* Başlık */}
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => window.location.assign('/lptrax')}
            className={`p-2 rounded-lg border ${sub} ${muted} hover:text-sky-400`} title="LP Tracker'a dön">
            <ArrowLeft size={16} />
          </button>
          <div>
            <h1 className="text-lg font-semibold flex items-center gap-2">
              <Wallet size={18} className="text-sky-400" /> Navi Takip
            </h1>
            <p className={`text-[11px] ${muted}`}>
              Lending / borrowing · saatlik snapshot {wallet && <>· <span className="font-mono">{shortId(wallet)}</span></>}
            </p>
          </div>
          <button onClick={() => window.location.assign('/suilend')} title="Suilend (Bluefin Lend) takibine geç"
            className={`ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs ${sub} ${muted} hover:text-sky-400`}>
            <Banknote size={13} /> Suilend
          </button>
          <button onClick={() => window.location.assign('/butce')} title="Bütçe — kişisel gelir/gider defteri"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs ${sub} ${muted} hover:text-violet-300`}>
            <CalendarDays size={13} /> Bütçe
          </button>
          <button onClick={load} disabled={loading}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs ${sub} ${muted} hover:text-sky-400 disabled:opacity-50`}>
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Yenile
          </button>
        </div>

        {!latest && (
          <p className={`text-sm ${muted} py-10 text-center`}>
            {loading ? 'Yükleniyor…' : 'Henüz snapshot yok. İlk kayıt saatlik döngüde alınacak.'}
          </p>
        )}

        {latest && (
          <>
            {/* Özet */}
            <div className={`rounded-xl border ${card} p-3 mb-3 grid grid-cols-2 md:grid-cols-4 gap-3`}>
              <div>
                <div className={`text-[11px] ${muted}`}>Teminat (supply)</div>
                <div className="text-lg font-semibold leading-tight text-emerald-400">{fmtUsd(latest.supplyUsd)}</div>
              </div>
              <div>
                <div className={`text-[11px] ${muted}`}>Borç (borrow)</div>
                <div className="text-lg font-semibold leading-tight text-rose-400">{fmtUsd(latest.borrowUsd)}</div>
              </div>
              <div>
                <div className={`text-[11px] ${muted}`}>Net değer</div>
                <div className="text-lg font-semibold leading-tight text-sky-400">{fmtUsd(latest.netUsd)}</div>
              </div>
              <div>
                <div className={`text-[11px] ${muted}`}>Snapshot</div>
                <div className="text-lg font-semibold leading-tight">{snaps.length}</div>
                <div className={`text-[11px] ${muted}`}>son: {fmtDate(latest.ts)}</div>
              </div>
            </div>

            {/* Net değer seyri */}
            <div className={`rounded-xl border ${card} p-3.5 mb-3`}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-sm font-medium">Net değer seyri</span>
                {series.length >= 2 && (
                  <span className={`text-xs flex items-center gap-1 ${netChange >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {netChange >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                    {fmtSigned(netChange, 2)} $
                  </span>
                )}
                <span className={`ml-auto text-[11px] ${muted}`}>
                  {series.length >= 2 ? `${fmtDate(first!.ts)} → ${fmtDate(latest.ts)}` : 'grafik için en az 2 snapshot gerekir'}
                </span>
              </div>
              {series.length >= 2
                ? <Sparkline points={series} dark={dark} />
                : <p className={`text-xs ${muted} py-6 text-center`}>
                    Snapshot birikince eğri burada çizilecek (saatte 1 kayıt).
                  </p>}
            </div>

            {/* Faiz / kullanıcı hareketi ayrışması */}
            {decomp.length > 0 && (
              <div className={`rounded-xl border ${card} p-3.5 mb-3`}>
                <div className="flex items-center gap-2 mb-2">
                  <Percent size={14} className="text-violet-400" />
                  <span className="text-sm font-medium">Değişimin ayrışması</span>
                  <span className={`text-[11px] ${muted}`}>ilk snapshot'tan bugüne</span>
                </div>
                <p className={`text-[11px] ${muted} mb-2`}>
                  Navi <code>scaled × endeks</code> tutar: scaled yalnız sen işlem yapınca, endeks yalnız faizden değişir.
                </p>
                <div className="space-y-1">
                  {decomp.map((d, i) => {
                    // Borç tarafında faizin ARTMASI senin MALİYETİN (borç büyüyor) → kırmızı.
                    // Teminat tarafında artış kazanç → yeşil.
                    const isCost = d.kind === 'borrow';
                    const good = isCost ? d.interest < 0 : d.interest >= 0;
                    return (
                      <div key={i} className={`flex items-center gap-3 text-xs px-2 py-1.5 rounded-md ${sub} border`}>
                        <span className="font-medium w-20 shrink-0">{d.symbol}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${isCost
                          ? 'bg-rose-500/15 text-rose-400 border border-rose-500/30'
                          : 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'}`}>{d.kind}</span>
                        <span className="tabular-nums">
                          {isCost ? 'ödenen faiz' : 'kazanılan faiz'}{' '}
                          <span className={good ? 'text-emerald-400' : 'text-rose-400'}>{fmtSigned(d.interest, 6)}</span>
                        </span>
                        {Math.abs(d.user) > 1e-9 && (
                          <span className={`tabular-nums ${muted} flex items-center gap-1`}>
                            <ArrowUpRight size={11} /> senin hareketin <span className={text}>{fmtSigned(d.user, 4)}</span>
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Varlık kırılımı */}
            <div className={`rounded-xl border ${card} p-3.5`}>
              <div className="text-sm font-medium mb-2">Varlıklar</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className={`${muted} text-left`}>
                      <th className="py-1.5 pr-3 font-medium">Varlık</th>
                      <th className="py-1.5 pr-3 font-medium text-right">Teminat</th>
                      <th className="py-1.5 pr-3 font-medium text-right">Borç</th>
                      <th className="py-1.5 pr-3 font-medium text-right">Fiyat</th>
                      <th className="py-1.5 font-medium text-right">Net USD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {latest.assets.filter(a => a.supply > 1e-6 || a.borrow > 1e-6).map(a => {
                      const net = (a.supplyUsd || 0) - (a.borrowUsd || 0);
                      return (
                        <tr key={a.assetId} className={`border-t ${dark ? 'border-[#0a2560]' : 'border-[#d4e0f0]'}`}>
                          <td className="py-1.5 pr-3 font-medium">{a.symbol}</td>
                          <td className="py-1.5 pr-3 text-right tabular-nums">{a.supply > 1e-6 ? fmt(a.supply) : '—'}</td>
                          <td className="py-1.5 pr-3 text-right tabular-nums text-rose-400">{a.borrow > 1e-6 ? fmt(a.borrow) : '—'}</td>
                          <td className={`py-1.5 pr-3 text-right tabular-nums ${muted}`}>
                            {a.priceUsd ? '$' + a.priceUsd.toLocaleString('en-US', { maximumFractionDigits: 6 }) : '—'}
                          </td>
                          <td className={`py-1.5 text-right tabular-nums font-medium ${net >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                            {a.priceUsd ? fmtUsd(net) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
